import { FC, useEffect, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Flex, TextInput } from 'portal-commons';
import { RcsCampaign } from '../../types';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';

interface SampleMessagesFormProps {
  campaign: RcsCampaign;
}

const MSG_MIN_LENGTH = 20;
const MSG_MAX_LENGTH = 1024;

const SampleMessagesForm: FC<SampleMessagesFormProps> = ({ campaign }) => {
  const { control, clearErrors, reset } = useFormContext();
  const defaultValues = useMemo(() => {
    return Object.keys(campaign)
      .filter((key) => /^sample\d+$/.test(key) && !!campaign[key])
      .reduce((acc, key) => {
        acc[key] = campaign[key];
        return acc;
      }, {} as Record<string, string>);
  }, [campaign]);

  useEffect(() => {
    clearErrors();
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: 'xl',
      }}
    >
      {Object.keys(defaultValues).map((key, index) => (
        <Controller
          control={control}
          name={key}
          rules={{
            validate: {
              isValidInput,
              isRequired: createRequiredValidator('Please type Sample Message'),
              withValidMinLength: createMinLengthValidator(MSG_MIN_LENGTH),
              withValidMaxLength: createMaxLengthValidator(MSG_MAX_LENGTH),
            },
          }}
          render={({ field, fieldState }) => (
            <TextInput
              required
              multiline
              {...field}
              key={key}
              label={`Sample Message ${index + 1}`}
              error={!!fieldState.error?.message}
              note={fieldState.error?.message}
              maxLength={MSG_MAX_LENGTH}
              data-testid={`sample${index + 1}`}
            />
          )}
        />
      ))}
    </Flex>
  );
};

export default SampleMessagesForm;
